import React, { useState, useRef, useEffect } from "react";
import CustomPhoneInput from './components/CustomPhoneInput';
import DynamicNavigation from "./DynamicNavigation";
import { Footer } from "./components";
import "./eligibility-button.css";
import { submitLead, updateLeadScore, captureLeadInstantly, pixelCaptureLead, newSubmissionId } from './admin/api';
import { sendCRMLead, getSubmitButtonLabel, getLastCta } from './services/crmLeadService';
import { useNavLinkClick } from "./hooks/useNavLinkClick";
import { useLocation } from "react-router-dom";
import TurnstileWidget from './components/TurnstileWidget';

const NAV_LINKS = [
  { id: "home", label: "Home", href: "/" },
  { id: "services", label: "Services", href: "/services" },
  { id: "about", label: "About", href: "/about" },
  { id: "contact", label: "Contact", href: "/contact" },
];
const LOGO = <img src="/logo.png" alt="Logo" className="h-10 w-auto" />;

// --- EXACT HERO/BANNER SECTION AS PER USER HTML/CSS ---
const BannerSection = () => (
  <section
    className="w-full bg-cover bg-center bg-no-repeat px-4 pb-10"
    style={{
      backgroundImage: "url('https://assets.prebuiltui.com/images/components/hero-section/hero-grid-gradient-img.png')",
      fontFamily: 'Poppins, sans-serif',
      minHeight: 520,
      paddingBottom: 0,
    }}
  >
    <div className="w-full mx-auto flex flex-col md:flex-row items-center justify-between gap-8 py-36 max-w-[1200px]">
      {/* Left: Heading */}
      <div className="flex-1 flex flex-col items-start justify-center">
        <h1 className="text-neutral-900 text-4xl md:text-5xl lg:text-[48px] leading-tight font-extrabold max-w-[610px]" style={{lineHeight:1.08}}>
          Australia Immigration<br />
          <span style={{color:'#7C3AED'}}>Points Calculator</span>
        </h1>
      </div>
      {/* Right: Banner Image */}
      <div className="flex-1 flex items-center justify-center">
        <img
          src="/banner/eligibility-hero-banner.png"
          alt="Australia Banner"
          style={{ width: 'clamp(220px, 32vw, 360px)', maxWidth: 360, minWidth: 180, height: 'auto', objectFit: 'contain', display: 'block' }}
        />
      </div>
    </div>
  </section>
);

// Australia Migration Eligibility Questions (12 steps) - UPDATED
const questions = [
  {
    id: 1,
    question: "Your age group",
    options: [
      { label: "18 to 24", value: 25 },
      { label: "25 to 32", value: 30 },
      { label: "33 to 39", value: 25 },
      { label: "40 to 44", value: 15 }
    ]
  },
  {
    id: 2,
    question: "Your highest level of education",
    options: [
      { label: "PhD", value: 20 },
      { label: "Masters", value: 15 },
      { label: "Bachelors", value: 15 },
      { label: "Diploma after Bachelors", value: 10 },
      { label: "Diploma after secondary", value: 10 }
    ]
  },
  {
    id: 3,
    question: "Your total work experience outside Australia",
    options: [
      { label: "Less than 1 year", value: 0 },
      { label: "1 year", value: 0 },
      { label: "2 years", value: 0 },
      { label: "3 years", value: 5 },
      { label: "4 years", value: 5 },
      { label: "5 years", value: 10 },
      { label: "6 years", value: 10 },
      { label: "7 years", value: 10 },
      { label: "8+ years", value: 15 }
    ]
  },
  {
    id: 4,
    question: "Your English proficiency",
    options: [
      { label: "Very High Proficiency (IELTS 8 / PTE 79)", value: 20 },
      { label: "High Proficiency (IELTS 7 / PTE 65)", value: 10 },
      { label: "Moderate Proficiency", value: 0 },
      { label: "Basic Proficiency", value: 0 },
      { label: "No Proficiency", value: 0 }
    ]
  },
  {
    id: 5,
    question: "Are you married?",
    options: [
      { label: "No", value: 10 },
      { label: "Yes", value: 5 }
    ]
  },
  {
    id: 6,
    question: "Do you have Australian work experience?",
    options: [
      { label: "0 years", value: 0 },
      { label: "1 year", value: 5 },
      { label: "3 years", value: 10 },
      { label: "5 years", value: 15 },
      { label: "8+ years", value: 20 }
    ]
  },
  {
    id: 7,
    question: "Did you study in Australia for at least two academic years?",
    options: [
      { label: "Yes", value: 5 },
      { label: "No", value: 0 }
    ]
  },
  {
    id: 8,
    question: "Do you hold a Masters by research or PhD from an Australian institution in STEM fields?",
    options: [
      { label: "Yes", value: 10 },
      { label: "No", value: 0 }
    ]
  },
  {
    id: 9,
    question: "Was your Australian study completed in regional Australia?",
    options: [
      { label: "Yes", value: 5 },
      { label: "No", value: 0 }
    ]
  },
  {
    id: 10,
    question: "Do you hold NAATI community language certification?",
    options: [
      { label: "Yes", value: 5 },
      { label: "No", value: 0 }
    ]
  },
  {
    id: 11,
    question: "Sponsorship preference",
    options: [
      { label: "State / Territory nomination", value: 5 },
      { label: "Regional state nomination", value: 15 },
      { label: "Close family sponsorship", value: 15 },
      { label: "No sponsorship", value: 0 }
    ]
  },
  {
    id: 12,
    question: "Did you complete a Professional Year Program in Australia?",
    options: [
      { label: "Yes", value: 5 },
      { label: "No", value: 0 }
    ]
  }
];

const navigationLinks = [
  { id: "home", label: "Home", href: "/" },
  { id: "services", label: "Services", href: "/services" },
  { id: "about", label: "About", href: "/about" },
  { id: "contact", label: "Contact", href: "/contact" },
];

const logo = <img src="/logo.png" alt="Logo" className="h-10 w-auto" />;

// Type definitions
interface AnswerOption {
  label: string;
  value: number;
}
interface Question {
  id: number;
  question: string;
  options: AnswerOption[];
}
interface LeadData {
  name: string;
  phone: string;
  email: string;
  country: string;
  countryCode: string;
}
// answers: key is question number, value is AnswerOption
const AustraliaMigrationEligibilityChecker = () => {
  const location = useLocation();
  const [stage, setStage] = useState<number>(1); // 1: lead form, 2: quiz/result
  const [leadData, setLeadData] = useState<LeadData>({ name: "", phone: "", email: "", country: "", countryCode: "" });
  const [leadError, setLeadError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [leadHoneypot, setLeadHoneypot] = useState<string>("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [formLoadedAt] = useState(() => Date.now());
  const submittingRef = useRef(false);
  const [leadInsertId, setLeadInsertId] = useState<number | null>(null);
  const [leadScoreToken, setLeadScoreToken] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<number>(1);
  const [answers, setAnswers] = useState<Record<number, AnswerOption>>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const countryRef = useRef<HTMLInputElement>(null);
  const totalQuestions = questions.length;

  // Server-side pixel capture: fires whenever the user pauses typing for 800ms.
  // This is the most reliable lead-capture path — a GET <img> request that
  // bypasses CORS, fetch, sendBeacon, Turnstile, and every JS error path.
  // Data lands in DynamoDB before the user even clicks Submit.
  useEffect(() => {
    const t = setTimeout(() => {
      pixelCaptureLead({
        name: leadData.name,
        email: leadData.email,
        phone: leadData.countryCode ? leadData.countryCode + leadData.phone : leadData.phone,
        country: leadData.country,
        country_code: leadData.countryCode || undefined,
        lead_type: 'eligibility',
        source_page: location.pathname,
        source_form: 'eligibility_gate_form',
      });
    }, 800);
    return () => clearTimeout(t);
  }, [leadData.name, leadData.email, leadData.phone, leadData.country, leadData.countryCode, location.pathname]);

  const handleLeadSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submittingRef.current) return;

    const submissionId = newSubmissionId();
    const lastCta = getLastCta();
    const trackingFields = {
      submission_id: submissionId,
      button_name: getSubmitButtonLabel(e, 'Check Eligibility'),
      cta_name: lastCta?.label,
      cta_page: lastCta?.page,
    };
    // INSTANT CAPTURE — also fire the pixel one more time at click time so
    // we have the FINAL values (not just the last debounced state).
    pixelCaptureLead({
      name: leadData.name,
      email: leadData.email,
      phone: leadData.countryCode ? leadData.countryCode + leadData.phone : leadData.phone,
      country: leadData.country,
      country_code: leadData.countryCode || undefined,
      lead_type: 'eligibility',
      source_page: location.pathname,
      source_form: 'eligibility_gate_form',
    });
    captureLeadInstantly({
      name: leadData.name,
      email: leadData.email,
      phone: leadData.countryCode ? leadData.countryCode + leadData.phone : leadData.phone,
      country: leadData.country,
      country_code: leadData.countryCode || undefined,
      lead_type: 'eligibility',
      source_page: location.pathname,
      source_form: 'eligibility_gate_form',
      form_position: 'stage_1',
      _formLoadedAt: formLoadedAt,
      ...trackingFields,
    });
    if (!leadData.name || !leadData.phone || !leadData.email) {
      setLeadError("All fields are required.");
      return;
    }
    submittingRef.current = true;
    setLoading(true);
    setLeadError("");
    // Honeypot check — do NOT silently drop. The instant-capture above
    // already saved the data; let the server flag it as honeypot via the
    // company_website field below.
    // (Removed the early-return that previously skipped submitLead.)
    // Submit to backend API — block progression if save fails so the lead is never lost silently
    const result = await submitLead({
      name: leadData.name,
      phone: leadData.countryCode ? leadData.countryCode + leadData.phone : leadData.phone,
      email: leadData.email,
      country: leadData.country,
      country_code: leadData.countryCode || undefined,
      lead_type: 'eligibility',
      source_page: location.pathname,
      source_form: 'eligibility_gate_form',
      form_position: 'stage_1',
      company_website: leadHoneypot || undefined,
      turnstileToken: turnstileToken || undefined,
      _formLoadedAt: formLoadedAt,
      ...trackingFields,
    });

    if (result.success || result.queued) {
      // Universal CRM sync — fire-and-forget; never blocks the eligibility quiz
      void sendCRMLead({
        name: leadData.name,
        email: leadData.email,
        phone: leadData.countryCode ? leadData.countryCode + leadData.phone : leadData.phone,
        lead_type: 'assessment',
        form_name: 'eligibility_gate_form',
        message: null,
        country: leadData.country,
        country_code: leadData.countryCode || undefined,
        form_position: 'stage_1',
        source_page: location.pathname,
        ...trackingFields,
      });
    }

    setLoading(false);
    submittingRef.current = false;
    // The new submitLead always saves to localStorage first, so even on a "queued"
    // (network failure) result the data is safe and will be auto-replayed. Allow the
    // user to continue the quiz in that case.
    if (!result.success && !result.queued) {
      setLeadError(result.error || "We couldn't save your details. Please try again in a few minutes or contact us directly.");
      return;
    }
    if (result.id) {
      setLeadInsertId(result.id);
      if (result.scoreToken) setLeadScoreToken(result.scoreToken);
    }
    setStage(2);
  };

  const handleOptionSelect = (option: AnswerOption) => {
    setAnswers({ ...answers, [currentQuestion]: option });
  };

  const handleNext = () => {
    if (!answers[currentQuestion]) return;
    setCurrentQuestion((q) => q + 1);
  };

  const handleBack = () => {
    setCurrentQuestion((q) => Math.max(q - 1, 1));
  };

  const handleRestart = () => {
    setStage(1);
    setLeadData({ name: "", phone: "", email: "", country: "", countryCode: "" });
    setAnswers({});
    setCurrentQuestion(1);
    setLeadInsertId(null);
    setLeadScoreToken(null);
  };

  const isComplete = currentQuestion > totalQuestions;
  // Calculate total points
  const totalPoints: number = Object.values(answers).reduce((sum, a) => sum + (a?.value || 0), 0);
  // Calculate max possible points (sum of highest value for each question)
  const maxPoints: number = questions.reduce((sum, q) => sum + Math.max(...q.options.map(o => o.value)), 0);
  // Visa recommendation logic as per new requirements
  const sponsorship: string = answers[11]?.label || "";
  const visaRecommendation = (points: number, sponsorship: string) => {
    if (points >= 80 && sponsorship === "No sponsorship") return "Subclass 189 (Skilled Independent)";
    if (points >= 65 && sponsorship === "State / Territory nomination") return "Subclass 190";
    if (sponsorship === "Regional state nomination" || sponsorship === "Close family sponsorship") return "Subclass 491";
    if (points < 65) return null;
    return null;
  };
  const visa: string | null = visaRecommendation(totalPoints, sponsorship);
  const eligible: boolean = totalPoints >= 65;

  // Save quiz score + visa recommendation when quiz completes
  useEffect(() => {
    if (isComplete && leadInsertId && leadScoreToken) {
      updateLeadScore(leadInsertId, totalPoints, visa || 'Not eligible', leadScoreToken)
        .then((ok) => {
          if (!ok) console.error('Error saving quiz results');
        });
    }
  }, [isComplete, leadInsertId, leadScoreToken, totalPoints, visa]);

  const onLinkClick = useNavLinkClick(navigationLinks);

  // --- MODERN PAGE STRUCTURE: BANNER + CENTERED FORM ---
  if (stage === 1) {
    // Show lead form
    return (
      <div style={{ fontFamily: 'Poppins, sans-serif', background: '#f7fafd', minHeight: '100vh' }}>
        <DynamicNavigation links={NAV_LINKS} logo={LOGO} />
        <BannerSection />
        {/* Eligibility Test Section */}
        <section style={{ background: '#F3F7FF', width: '100%', minHeight: 320, padding: '0' }}>
          <div className="max-w-[1200px] mx-auto px-4" style={{ paddingTop: 100, paddingBottom: 60 }}>
            <h2 style={{ fontSize: 28, fontWeight: 600, color: '#111827', marginBottom: 28, textAlign: 'left' }}>Check Your Eligibility</h2>
            <form className="w-full" style={{ marginBottom: 0 }} onSubmit={handleLeadSubmit}>
              {/* Honeypot */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
                <input type="text" name="company_website" value={leadHoneypot} onChange={(e) => setLeadHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
              </div>
              <div className="eligibility-form-grid">
                <div className="form-field">
                  <label className="form-label">Full Name</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#bdbdbd" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>
                    </span>
                    <input type="text" value={leadData.name} onChange={e => setLeadData({ ...leadData, name: e.target.value })} ref={nameRef} required placeholder="Enter your name" />
                  </div>
                </div>
                <div className="form-field">
                  <label className="form-label">Phone Number</label>
                  <CustomPhoneInput
                    value={leadData.phone}
                    onChange={val => setLeadData({ ...leadData, phone: val })}
                    onCountryChange={code => setLeadData(ld => ({ ...ld, countryCode: code }))}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Email</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#bdbdbd" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
                    </span>
                    <input type="email" value={leadData.email} onChange={e => setLeadData({ ...leadData, email: e.target.value })} ref={emailRef} required placeholder="Enter your email" />
                  </div>
                </div>
                <div className="form-field">
                  <label className="form-label">Country of Residence</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#bdbdbd" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                    </span>
                    <input type="text" value={leadData.country} onChange={e => setLeadData({ ...leadData, country: e.target.value })} ref={countryRef} placeholder="Enter your country" />
                  </div>
                </div>
              </div>
              {leadError && <div className="text-red-600 mb-3 font-medium" style={{ textAlign: 'left' }}>{leadError}</div>}
              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <TurnstileWidget
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken("")}
                />
              </div>
              <button type="submit" disabled={loading} className="cssbuttons-io-button mt-7 eligibility-blue-btn" style={{ marginTop: 24 }}>
                Check Eligibility
                <span className="icon">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#2563eb"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7-7 7 7-7 7" /></svg>
                </span>
              </button>
              <style>{`
                                .eligibility-form-grid {
                                  display: grid;
                                  grid-template-columns: 1fr 1fr;
                                  gap: 20px;
                                  width: 100%;
                                  max-width: 800px;
                                }
                                @media (max-width: 768px) {
                                  .eligibility-form-grid {
                                    grid-template-columns: 1fr;
                                  }
                                }
                                .form-field {
                                  width: 100%;
                                  display: flex;
                                  flex-direction: column;
                                }
                                .form-label {
                                  font-size: 1rem;
                                  font-weight: 500;
                                  color: #374151;
                                  margin-bottom: 8px;
                                  text-align: left;
                                }
                                .input-wrapper {
                                  height: 52px;
                                  width: 100%;
                                  border-radius: 14px;
                                  padding: 0 16px;
                                  background: #fff;
                                  border: 1px solid #e0e0e0;
                                  display: flex;
                                  align-items: center;
                                  gap: 10px;
                                  box-sizing: border-box;
                                  position: relative;
                                }
                                .input-icon {
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  color: #bdbdbd;
                                  position: static;
                                  margin-right: 6px;
                                }
                                .input-wrapper input {
                                  flex: 1;
                                  border: none;
                                  outline: none;
                                  background: transparent;
                                  font-size: 1rem;
                                  color: #222;
                                  font-weight: 500;
                                  height: 100%;
                                  padding: 0;
                                  box-sizing: border-box;
                                }
                                .phone-input {
                                  width: 100%;
                                  height: 100%;
                                  border: none;
                                  outline: none;
                                  background: transparent;
                                  font-size: 1rem;
                                  color: #222;
                                  font-weight: 500;
                                  padding: 0;
                                  box-sizing: border-box;
                                }
                .cssbuttons-io-button {
                  background: #2563eb;
                  color: white;
                  font-family: inherit;
                  padding: 0.35em;
                  padding-left: 1.2em;
                  font-size: 17px;
                  font-weight: 500;
                  border-radius: 0.9em;
                  border: none;
                  letter-spacing: 0.05em;
                  display: flex;
                  align-items: center;
                  box-shadow: inset 0 0 1.6em -0.6em #2563eb33;
                  overflow: hidden;
                  position: relative;
                  height: 2.8em;
                  padding-right: 3.3em;
                  cursor: pointer;
                }
                .eligibility-blue-btn {
                  background: #2563eb !important;
                  color: #fff !important;
                  border: none !important;
                }
                .cssbuttons-io-button .icon svg {
                  color: #2563eb;
                }
                .cssbuttons-io-button .icon {
                  background: white;
                  margin-left: 1em;
                  position: absolute;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  height: 2.2em;
                  width: 2.2em;
                  border-radius: 0.7em;
                  box-shadow: 0.1em 0.1em 0.6em 0.2em #7b52b9;
                  right: 0.3em;
                  transition: all 0.3s;
                }
                .cssbuttons-io-button:hover .icon {
                  width: calc(100% - 0.6em);
                }
                .cssbuttons-io-button .icon svg {
                  width: 1.1em;
                  transition: transform 0.3s;
                  color: #7b52b9;
                }
                .cssbuttons-io-button:hover .icon svg {
                  transform: translateX(0.1em);
                }
                .cssbuttons-io-button:active .icon {
                  transform: scale(0.95);
                }
              `}</style>
            </form>
          </div>
        </section>
        {/* Disclaimer */}
        <div className="w-full bg-gray-50 border-t border-gray-200 py-6 px-4">
          <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto text-center">
            <strong className="text-gray-500">Disclaimer:</strong> This calculator provides an indicative score only and is intended as a general guide. It does not constitute formal immigration advice or an official points assessment. Actual eligibility may differ based on current Department of Home Affairs criteria and individual circumstances. Please consult a registered migration agent (MARA) for professional advice.
          </p>
        </div>
        <Footer />
        <style>{`
          .fade-in { animation: fadeIn 0.8s ease; }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: none; } }
        `}</style>
      </div>
    );
  }
  // Show quiz/results if stage === 2
  return (
    <div style={{ fontFamily: 'Poppins, sans-serif', background: '#f7fafd', minHeight: '100vh' }}>
      <DynamicNavigation links={NAV_LINKS} logo={LOGO} />
      <BannerSection />
      <div className="w-full max-w-[1200px] mx-auto flex flex-col lg:flex-row items-start justify-between gap-8 fade-in" style={{padding: '40px 20px'}}>
        {/* Eligibility Section: Show result or quiz */}
        <div className="flex-1 min-w-0">
          <div className="w-full bg-white border border-gray-100 rounded-3xl p-8 md:p-10" style={{boxShadow: 'none'}}>
            {currentQuestion > totalQuestions ? (
              <div>
                <div className="text-2xl font-semibold mb-5 text-center text-[#222] tracking-tight">Thank you <span style={{ fontWeight: 600 }}>{leadData.name}</span> for completing the migration assessment.</div>
                <div style={{ display: 'flex', gap: 48, alignItems: 'flex-start', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Left Card */}
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 18, padding: 36, minWidth: 270, maxWidth: 340, boxShadow: '0 2px 12px 0 #f3f3f3', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                    <img src={eligible ? '/assets/feature-section/aus-map-green.png' : '/assets/feature-section/aus-map-red.png'} alt="Australia Map" style={{ width: 120, marginBottom: 18 }} />
                    <div style={{ fontSize: 16, color: '#555', marginBottom: 8, textAlign: 'center', fontWeight: 500 }}>Your eligibility score to Migrate to Australia</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 8 }}>
                      <div style={{ background: eligible ? '#22c55e' : '#ef4444', color: '#fff', fontWeight: 700, fontSize: 36, borderRadius: 8, minWidth: 80, minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', letterSpacing: 1 }}>{totalPoints}</div>
                    </div>
                    <div style={{ fontSize: 15, color: '#888', marginBottom: 18 }}>Minimum Threshold <b>65</b></div>
                    <div style={{ width: '100%', borderTop: '1px solid #e5e7eb', margin: '18px 0 18px 0' }} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <div style={{ fontSize: 16, color: '#222', fontWeight: 500 }}>Australia Immigration points factor</div>
                      <button style={{ background: '#3b4890', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 28px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>View</button>
                    </div>
                  </div>
                  {/* Right Section */}
                  <div style={{ flex: 1, minWidth: 220, paddingTop: 12 }}>
                    <div style={{ fontSize: 32, fontWeight: 700, color: eligible ? '#16a34a' : '#dc2626', marginBottom: 8 }}>
                      {eligible ? 'Congratulations!' : 'Unfortunately'}
                    </div>
                    <div style={{ fontSize: 18, color: '#222', marginBottom: 18, fontWeight: 500 }}>
                      {eligible
                        ? (<span>Based on your answers, you are eligible to migrate to Australia under the <b>{visa ? visa : 'relevant visa'}</b>.</span>)
                        : (<span>Based on your answers, your score is below the minimum requirement, so you are currently not eligible. Our team will review your profile and contact you with possible options.</span>)}
                    </div>
                    <div style={{ fontSize: 15, color: '#222', marginBottom: 32 }}>
                      Please contact us for detailed check<br />
                      <span style={{ fontWeight: 600 }}>+91 90376 62324</span><br />
                      1800 80 4652<br />
                      hello@holaworld.in
                    </div>
                    <div style={{ fontSize: 13, color: '#444', marginTop: 32, marginBottom: 0 }}>
                      Disclaimer: This is an estimated score based on your responses. Final eligibility and points may vary after expert evaluation and official assessment criteria.
                    </div>
                    <div style={{ marginTop: 32 }}>
                      <button
                        style={{ padding: '12px 32px', borderRadius: 24, fontWeight: 600, fontSize: 17, background: 'linear-gradient(90deg,#8B5CF6 0%,#4F39F6 100%)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                        onClick={handleRestart}
                      >
                        Restart Calculator
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#fff"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7-7 7 7-7 7" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="text-center text-2xl font-semibold mb-2 text-[#222] tracking-tight">STEP {currentQuestion} OF {totalQuestions}</div>
                  <div className="w-full h-3 bg-[#e5e7eb] rounded-full overflow-hidden mb-4">
                    <div
                      style={{
                        width: `${(Object.keys(answers).length / totalQuestions) * 100}%`,
                        background: totalPoints >= 65 ? '#22c55e' : '#ef4444',
                        height: '100%',
                        borderRadius: 12,
                        transition: 'width 0.3s, background 0.3s'
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                      <span>Answered: {Object.keys(answers).length}/{totalQuestions}</span>
                      <span>Points:</span>
                  </div>
                </div>
                {/* Compact mobile-only score indicator */}
                <div className="flex lg:hidden flex-col items-center mb-5 rounded-2xl overflow-hidden" style={{ border: '1px solid #ede9fe', boxShadow: '0 2px 16px rgba(79,57,246,0.08)' }}>
                  {/* Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #4F39F6 0%, #7C3AED 100%)',
                    width: '100%',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    justifyContent: 'center'
                  }}>
                    <img src="https://flagcdn.com/au.svg" alt="Australia Flag" style={{ width: 22, borderRadius: 2 }} />
                    <div>
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', lineHeight: 1.2 }}>Get Evaluated For</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', lineHeight: 1.2 }}>Australia</div>
                    </div>
                  </div>
                  {/* Score body */}
                  <div style={{ background: '#fff', width: '100%', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Your Current Score</div>
                    <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
                      <svg width="44" height="44" style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx="22" cy="22" r="17" stroke="#f3f4f6" strokeWidth="4" fill="none" />
                        <circle
                          cx="22" cy="22" r="17"
                          stroke={totalPoints >= 65 ? '#22c55e' : '#ef4444'}
                          strokeWidth="4"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 17}
                          strokeDashoffset={2 * Math.PI * 17 * (1 - Math.min(1, totalPoints / maxPoints))}
                          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: totalPoints >= 65 ? '#16a34a' : '#dc2626', transition: 'color 0.3s' }}>
                          {totalPoints}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-6 text-xl font-semibold text-neutral-900 text-center" style={{ fontFamily: 'Manrope, Poppins, sans-serif' }}>{questions[currentQuestion - 1]?.question}</div>
                <div className="flex flex-wrap gap-4 mb-10 justify-center">
                  {questions[currentQuestion - 1]?.options.map((option, idx) => (
                    <button
                      key={idx}
                      className={`border-2 rounded-full py-3 px-8 text-lg font-medium transition-colors duration-150 focus:outline-none min-w-[140px] text-center ${answers[currentQuestion]?.label === option.label ? 'border-[#4F39F6] text-[#4F39F6] font-bold bg-[#f4f4ff]' : 'border-[#e0e0e0] text-[#222] bg-white hover:border-[#4F39F6] hover:text-[#4F39F6]'}`}
                      onClick={() => handleOptionSelect(option)}
                      type="button"
                      style={{ fontFamily: 'Manrope, Poppins, sans-serif', boxShadow: 'none' }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-4 mt-4 justify-center">
                  {currentQuestion > 1 && (
                    <button
                      className="px-10 py-3 rounded-full font-semibold bg-[#e5e7eb] text-[#4F39F6] hover:bg-[#ede9fe] transition-all duration-150"
                      onClick={handleBack}
                      type="button"
                    >
                      &#60; Back
                    </button>
                  )}
                  <button
                    className="px-10 py-3 rounded-full font-semibold bg-gradient-to-r from-[#8B5CF6] to-[#4F39F6] text-white hover:from-[#7c3aed] hover:to-[#3e2fcc] transition-all duration-200 flex items-center gap-2"
                    onClick={handleNext}
                    disabled={!answers[currentQuestion]}
                    type="button"
                    style={{ opacity: !answers[currentQuestion] ? 0.5 : 1 }}
                  >
                    Next <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#fff"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7-7 7 7-7 7" /></svg>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {/* Right: Points Summary Card (show only during quiz, hidden on mobile) */}
        {currentQuestion <= totalQuestions && (
          <div className="hidden lg:block lg:w-auto shrink-0" style={{
            width: 300,
            background: '#fff',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 4px 32px rgba(79,57,246,0.10)',
            border: '1px solid #ede9fe',
            boxSizing: 'border-box',
            marginTop: 0
          }}>
            {/* Card Header */}
            <div style={{
              background: 'linear-gradient(135deg, #4F39F6 0%, #7C3AED 100%)',
              padding: '24px 20px 20px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 4
              }}>
                <img src="https://flagcdn.com/au.svg" alt="Australia Flag" style={{ width: 28, borderRadius: 3 }} />
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Get Evaluated For</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#fff', letterSpacing: '-0.3px' }}>Australia</div>
            </div>
            {/* Card Body */}
            <div style={{ padding: '24px 20px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>Your Current Score</div>
              {/* Circular gauge */}
              <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto 16px auto' }}>
                <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="70" cy="70" r="58" stroke="#f3f4f6" strokeWidth="10" fill="none" />
                  <circle
                    cx="70" cy="70" r="58"
                    stroke={totalPoints >= 65 ? '#22c55e' : totalPoints >= 40 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="10"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 58}
                    strokeDashoffset={2 * Math.PI * 58 * (1 - Math.min(1, totalPoints / maxPoints))}
                    style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s' }}
                  />
                </svg>
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                }}>
                  <span style={{
                    fontSize: 42, fontWeight: 800, lineHeight: 1,
                    color: totalPoints >= 65 ? '#16a34a' : totalPoints >= 40 ? '#d97706' : '#dc2626'
                  }}>
                    {totalPoints.toString().padStart(2, '0')}
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginTop: 2 }}>points</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Disclaimer */}
      <div className="w-full bg-gray-50 border-t border-gray-200 py-6 px-4">
        <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto text-center">
          <strong className="text-gray-500">Disclaimer:</strong> This calculator provides an indicative score only and is intended as a general guide. It does not constitute formal immigration advice or an official points assessment. Actual eligibility may differ based on current Department of Home Affairs criteria and individual circumstances. Please consult a registered migration agent (MARA) for professional advice.
        </p>
      </div>
      <Footer />
    </div>
  );
  // Show lead form if stage === 1
  if (stage === 1) {
    return (
      <div style={{ fontFamily: 'Poppins, sans-serif', background: '#f7fafd', minHeight: '100vh' }}>
        <DynamicNavigation links={NAV_LINKS} logo={LOGO} />
        <BannerSection />
        {/* Eligibility Test Section */}
        <section style={{ background: '#F3F7FF', width: '100%', minHeight: 320, padding: '0' }}>
          <div className="max-w-[1200px] mx-auto px-4" style={{ paddingTop: 100, paddingBottom: 60 }}>
            <h2 style={{ fontSize: 28, fontWeight: 600, color: '#111827', marginBottom: 28, textAlign: 'left' }}>Check Your Eligibility</h2>
            <form className="w-full" style={{ marginBottom: 0 }} onSubmit={handleLeadSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" style={{ columnGap: 24, rowGap: 18, maxWidth: 800 }}>
                <div className="flex flex-col">
                  <label className="mb-2 font-medium text-gray-700">Full Name</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#bdbdbd" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>
                    </span>
                    <input type="text" className="w-full pl-10 pr-4" style={{ height: 46, borderRadius: 12, background: '#fff', border: '1px solid #E5E7EB', color: '#222', fontSize: 16, fontWeight: 500, outline: 'none' }} value={leadData.name} onChange={e => setLeadData({ ...leadData, name: e.target.value })} ref={nameRef} required placeholder="Enter your name" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 font-medium text-gray-700">Phone Number</label>
                  <CustomPhoneInput
                    value={leadData.phone}
                    onChange={val => setLeadData({ ...leadData, phone: val })}
                    onCountryChange={code => setLeadData(ld => ({ ...ld, countryCode: code }))}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 font-medium text-gray-700">Email</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#bdbdbd" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
                    </span>
                    <input type="email" className="w-full pl-10 pr-4" style={{ height: 46, borderRadius: 12, background: '#fff', border: '1px solid #E5E7EB', color: '#222', fontSize: 16, fontWeight: 500, outline: 'none' }} value={leadData.email} onChange={e => setLeadData({ ...leadData, email: e.target.value })} ref={emailRef} required placeholder="Enter your email" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 font-medium text-gray-700">Country of Residence</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#bdbdbd" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                    </span>
                    <input type="text" className="w-full pl-10 pr-4" style={{ height: 46, borderRadius: 12, background: '#fff', border: '1px solid #E5E7EB', color: '#222', fontSize: 16, fontWeight: 500, outline: 'none' }} value={leadData.country} onChange={e => setLeadData({ ...leadData, country: e.target.value })} ref={countryRef} placeholder="Enter your country" />
                  </div>
                </div>
              </div>
              {leadError && <div className="text-red-600 mb-3 font-medium" style={{ textAlign: 'left' }}>{leadError}</div>}
              <button type="submit" disabled={loading} className="cssbuttons-io-button mt-7" style={{ marginTop: 24 }}>
                Check Eligibility
                <span className="icon">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#7C3AED"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7-7 7 7-7 7" /></svg>
                </span>
              </button>
              <style>{`
                .cssbuttons-io-button {
                  background: #a370f0;
                  color: white;
                  font-family: inherit;
                  padding: 0.35em;
                  padding-left: 1.2em;
                  font-size: 17px;
                  font-weight: 500;
                  border-radius: 0.9em;
                  border: none;
                  letter-spacing: 0.05em;
                  display: flex;
                  align-items: center;
                  box-shadow: inset 0 0 1.6em -0.6em #714da6;
                  overflow: hidden;
                  position: relative;
                  height: 2.8em;
                  padding-right: 3.3em;
                  cursor: pointer;
                }
                .cssbuttons-io-button .icon {
                  background: white;
                  margin-left: 1em;
                  position: absolute;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  height: 2.2em;
                  width: 2.2em;
                  border-radius: 0.7em;
                  box-shadow: 0.1em 0.1em 0.6em 0.2em #7b52b9;
                  right: 0.3em;
                  transition: all 0.3s;
                }
                .cssbuttons-io-button:hover .icon {
                  width: calc(100% - 0.6em);
                }
                .cssbuttons-io-button .icon svg {
                  width: 1.1em;
                  transition: transform 0.3s;
                  color: #7b52b9;
                }
                .cssbuttons-io-button:hover .icon svg {
                  transform: translateX(0.1em);
                }
                .cssbuttons-io-button:active .icon {
                  transform: scale(0.95);
                }
              `}</style>
            </form>
          </div>
        </section>
        <style>{`
          .fade-in { animation: fadeIn 0.8s ease; }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: none; } }
        `}</style>
      </div>
    );
  }

  // Main wizard/questionnaire UI
  return (
    <div style={{ fontFamily: 'Poppins, sans-serif', background: '#f7fafd', minHeight: '100vh' }}>
      <DynamicNavigation links={NAV_LINKS} logo={LOGO} />
      <BannerSection />
      <div className="w-full max-w-[1200px] mx-auto flex flex-col md:flex-row items-start justify-between gap-8 fade-in" style={{padding: '40px 20px'}}>
        {/* Left: Main Form */}
        <div className="flex-1 min-w-0">
          <div className="w-full bg-white border border-gray-100 rounded-3xl p-8 md:p-10" style={{boxShadow: 'none'}}>
            <div className="mb-8">
              <div className="text-center text-2xl font-semibold mb-2 text-[#222] tracking-tight">STEP {currentQuestion} OF {totalQuestions}</div>
              <div className="w-full h-3 bg-[#e5e7eb] rounded-full overflow-hidden mb-4">
                <div
                  style={{
                    width: `${(Object.keys(answers).length / totalQuestions) * 100}%`,
                    background: totalPoints >= 65 ? '#22c55e' : '#ef4444',
                    height: '100%',
                    borderRadius: 12,
                    transition: 'width 0.3s, background 0.3s'
                  }}
                />
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                  <span>Answered: {Object.keys(answers).length}/{totalQuestions}</span>
                  <span>Points:</span>
              </div>
              {/* Points circle removed from main form area. Only shown in right summary card. */}
            </div>
            <div className="mb-6 text-xl font-semibold text-neutral-900 text-center" style={{ fontFamily: 'Manrope, Poppins, sans-serif' }}>{questions[currentQuestion - 1]?.question}</div>
            <div className="flex flex-wrap gap-4 mb-10 justify-center">
              {questions[currentQuestion - 1]?.options.map((option, idx) => (
                <button
                  key={idx}
                  className={`border-2 rounded-full py-3 px-8 text-lg font-medium transition-colors duration-150 focus:outline-none min-w-[140px] text-center ${answers[currentQuestion]?.label === option.label ? 'border-[#4F39F6] text-[#4F39F6] font-bold bg-[#f4f4ff]' : 'border-[#e0e0e0] text-[#222] bg-white hover:border-[#4F39F6] hover:text-[#4F39F6]'}`}
                  onClick={() => handleOptionSelect(option)}
                  type="button"
                  style={{ fontFamily: 'Manrope, Poppins, sans-serif', boxShadow: 'none' }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex gap-4 mt-4 justify-center">
              {currentQuestion > 1 && (
                <button
                  className="px-10 py-3 rounded-full font-semibold bg-[#e5e7eb] text-[#4F39F6] hover:bg-[#ede9fe] transition-all duration-150"
                  onClick={handleBack}
                  type="button"
                >
                  &#60; Back
                </button>
              )}
              <button
                className="px-10 py-3 rounded-full font-semibold bg-gradient-to-r from-[#8B5CF6] to-[#4F39F6] text-white hover:from-[#7c3aed] hover:to-[#3e2fcc] transition-all duration-200 flex items-center gap-2"
                onClick={handleNext}
                disabled={!answers[currentQuestion]}
                type="button"
                style={{ opacity: !answers[currentQuestion] ? 0.5 : 1 }}
              >
                Next <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#fff"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7-7 7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        </div>
        {/* Right: Points Summary Card */}
        <div className="w-full lg:w-auto shrink-0" style={{
          width: 300,
          background: '#fff',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 4px 32px rgba(79,57,246,0.10)',
          border: '1px solid #ede9fe',
          boxSizing: 'border-box'
        }}>
          {/* Card Header */}
          <div style={{
            background: 'linear-gradient(135deg, #4F39F6 0%, #7C3AED 100%)',
            padding: '24px 20px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 4
            }}>
              <img src="https://flagcdn.com/au.svg" alt="Australia Flag" style={{ width: 28, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Get Evaluated For</div>
            <div style={{ fontWeight: 700, fontSize: 20, color: '#fff', letterSpacing: '-0.3px' }}>Australia</div>
          </div>
          {/* Card Body */}
          <div style={{ padding: '24px 20px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>Your Current Score</div>
            <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto 16px auto' }}>
              <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="70" cy="70" r="58" stroke="#f3f4f6" strokeWidth="10" fill="none" />
                <circle
                  cx="70" cy="70" r="58"
                  stroke={totalPoints >= 65 ? '#22c55e' : totalPoints >= 40 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 58}
                  strokeDashoffset={2 * Math.PI * 58 * (1 - Math.min(1, totalPoints / maxPoints))}
                  style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s' }}
                />
              </svg>
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{
                  fontSize: 42, fontWeight: 800, lineHeight: 1,
                  color: totalPoints >= 65 ? '#16a34a' : totalPoints >= 40 ? '#d97706' : '#dc2626'
                }}>
                  {totalPoints.toString().padStart(2, '0')}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginTop: 2 }}>points</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AustraliaMigrationEligibilityChecker;